import { useCallback, useEffect, useRef } from "react";
import {
  caretOffsetIn,
  type DictationTarget,
  insertIntoContentEditable,
  insertIntoTextControl,
  isTextControl,
  restoreCaretIn,
} from "@/lib/insert-at-cursor";
import {
  type DictationError,
  type DictationStatus,
  useDictation,
} from "./useDictation";

export type FieldDictation = {
  status: DictationStatus;
  interim: string;
  error: DictationError | null;
  elapsedSeconds: number;
  isActive: boolean;
  toggle: () => void;
  stop: () => void;
  // Attach to the mic button: focus moving to it does not stop dictation.
  buttonRef: React.MutableRefObject<HTMLButtonElement | null>;
};

type Options = {
  // The field to dictate into: an input, a textarea or a contenteditable.
  getTarget: () => DictationTarget | null;
  language?: string;
};

/**
 * Live dictation into any text field. Final words are inserted at the cursor
 * as each spoken segment completes (the field's input event fires, as when
 * typing); words still being recognised are only exposed as `interim`.
 * Dictation stops when focus leaves the field or on Escape, and at the same
 * 3-minute cap as the assistant.
 */
export const useFieldDictation = ({
  getTarget,
  language,
}: Options): FieldDictation => {
  const dictation = useDictation({ language });
  const { status, text, stop } = dictation;
  const isActive = status !== "idle";
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const targetRef = useRef<DictationTarget | null>(null);
  // Final words already inserted, and where the cursor was left.
  const committedRef = useRef("");
  const selectionRef = useRef({ start: 0, end: 0 });
  const offsetRef = useRef(0);
  const stopRef = useRef(stop);
  stopRef.current = stop;

  const insert = useCallback((words: string): void => {
    const target = targetRef.current;
    if (!target?.isConnected || !words.trim()) return;
    if (isTextControl(target)) {
      const caret = insertIntoTextControl(target, words, selectionRef.current);
      selectionRef.current = { start: caret, end: caret };
    } else {
      offsetRef.current = insertIntoContentEditable(
        target,
        words,
        offsetRef.current,
      );
    }
  }, []);

  // Commit each newly completed segment. The hook's text only grows while
  // listening; at the very end it may be replaced by the provider's full raw
  // transcript, which repeats words already inserted, so it is used only
  // when nothing was inserted yet.
  useEffect(() => {
    if (!text) return;
    const committed = committedRef.current;
    if (text.startsWith(committed)) {
      insert(text.slice(committed.length));
      committedRef.current = text;
    } else if (!committed) {
      insert(text);
      committedRef.current = text;
    }
  }, [text, insert]);

  // Stop when focus leaves the field (except to its own mic button) or on
  // Escape. Only while the microphone is open; finishing carries on.
  const listening = status === "connecting" || status === "listening";
  useEffect(() => {
    const target = targetRef.current;
    if (!listening || !target) return;
    let blurCheck: number | undefined;
    const onFocusOut = (): void => {
      // Decide once focus has settled. The window losing focus (the
      // browser's microphone prompt, switching to another app) keeps the
      // field focused inside the page and does not stop dictation.
      window.clearTimeout(blurCheck);
      blurCheck = window.setTimeout(() => {
        if (!document.hasFocus()) return;
        const now = document.activeElement;
        if (now && (now === buttonRef.current || target.contains(now))) return;
        stopRef.current();
      }, 0);
    };
    const onKeyDown = (event: Event): void => {
      if ((event as KeyboardEvent).key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      stopRef.current();
    };
    // Remember where the cursor is, for words that land after a blur.
    const onSelect = (): void => {
      if (isTextControl(target)) {
        selectionRef.current = {
          start: target.selectionStart ?? target.value.length,
          end: target.selectionEnd ?? target.value.length,
        };
      } else {
        offsetRef.current = caretOffsetIn(target) ?? offsetRef.current;
      }
    };
    const button = buttonRef.current;
    target.addEventListener("focusout", onFocusOut);
    target.addEventListener("keydown", onKeyDown);
    target.addEventListener("keyup", onSelect);
    target.addEventListener("mouseup", onSelect);
    target.addEventListener("select", onSelect);
    button?.addEventListener("keydown", onKeyDown);
    button?.addEventListener("focusout", onFocusOut);
    return () => {
      window.clearTimeout(blurCheck);
      target.removeEventListener("focusout", onFocusOut);
      target.removeEventListener("keydown", onKeyDown);
      target.removeEventListener("keyup", onSelect);
      target.removeEventListener("mouseup", onSelect);
      target.removeEventListener("select", onSelect);
      button?.removeEventListener("keydown", onKeyDown);
      button?.removeEventListener("focusout", onFocusOut);
    };
  }, [listening]);

  const start = (): void => {
    const target = getTarget();
    if (!target) return;
    targetRef.current = target;
    committedRef.current = "";
    // A field keeps its cursor while focus is on its own mic button (a
    // keyboard user tabbed to it), so that counts as having the cursor.
    const focused =
      document.activeElement === target ||
      target.contains(document.activeElement) ||
      document.activeElement === buttonRef.current;
    // Dictation goes where the cursor is; with no cursor in the field, at
    // the end of what is already there.
    if (isTextControl(target)) {
      const end = target.value.length;
      selectionRef.current = focused
        ? {
            start: target.selectionStart ?? end,
            end: target.selectionEnd ?? end,
          }
        : { start: end, end };
      target.focus();
      target.setSelectionRange(
        selectionRef.current.start,
        selectionRef.current.end,
      );
    } else {
      const end = (target.textContent ?? "").length;
      offsetRef.current = caretOffsetIn(target) ?? end;
      target.focus();
      restoreCaretIn(target, offsetRef.current);
    }
    void dictation.start();
  };

  const toggle = (): void => {
    if (isActive) stop();
    else start();
  };

  return {
    status,
    interim: dictation.interim,
    error: dictation.error,
    elapsedSeconds: dictation.elapsedSeconds,
    isActive,
    toggle,
    stop,
    buttonRef,
  };
};
